import type { CaseStudy } from '../types';

export const hatchWaxman: CaseStudy = {
  id: 'hatch-waxman',
  order: 17,
  title: 'Hatch-Waxman 法案',
  english: 'The Hatch-Waxman Act',
  category: 'power',
  era: '1984 — 今',
  region: '美国',
  accent: '#7a5b76',
  hook: '专利药的高价不是单一“贪婪”，而是专利期、审批、保险与仿制药进入规则共同塑造的结果。',
  question: '同一套法律，如何同时奖励新药研发、又让便宜仿制药更快进入市场？',
  summary:
    '美国 1984 年的 Hatch-Waxman 法案建立了仿制药简化申请与专利期补偿机制。它在创新激励与可及性之间做交易，也制造了专利链接、首仿独占和“常青化”等新的博弈空间。',
  thesis: '药品价格是法律设计出来的：专利保护、审批期限、仿制药准入和保险支付共同决定谁在什么时候能卖什么。',
  quote: '法律不能同时让创新无限期受保护、又让仿制药立刻便宜；它只能在两者之间分配时间。',
  isNew: true,
  entryNodeIds: ['hatch-patent', 'hatch-approval', 'hatch-generic'],
  focusNodeId: 'hatch-generic',
  lensIds: ['regulation', 'incentives', 'asymmetry', 'commitment'],
  opportunityIds: ['opp-generic-compliance'],
  sourceIds: ['fda-hatch-waxman', 'ftc-generic-drugs'],
  facts: [
    {
      text: '美国《药品价格竞争与专利期恢复法》（Hatch-Waxman Act）于 1984 年通过，建立了简化新药申请（ANDA）路径，并对部分专利期进行补偿。',
      sourceIds: ['fda-hatch-waxman'],
    },
    {
      text: '该法为首个挑战专利成功的仿制药申请者提供一段市场独占期；具体期限、适用条件和诉讼时点以法条与 FDA 规则为准。',
      sourceIds: ['fda-hatch-waxman'],
    },
    {
      text: '仿制药在美国处方量中占很高比例，但价格与支出份额受药品种类、保险、采购和专利状态影响；不能用“仿制药一定便宜”概括所有药物。',
      sourceIds: ['fda-hatch-waxman', 'ftc-generic-drugs'],
    },
  ],
  nodes: [
    {
      id: 'hatchwaxman',
      caseId: 'hatch-waxman',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: 'Hatch-Waxman 法案',
      summary: '用一套法律在专利保护和仿制药竞争之间分配时间。',
      detail:
        '在 1984 年之前，美国新药审批时间长、专利期被审批消耗，仿制药又要重复做大量试验。Hatch-Waxman 试图同时解决两个问题：让创新药获得一定补偿，让仿制药通过简化路径更快进入。结果不是“自由市场”，而是一套高度法律化的利益分配。',
      boundary: '本案例只讨论美国联邦药品专利与审批框架，不构成法律意见；具体案件需查法条、判例与 FDA 文件。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['fda-hatch-waxman'],
    },
    {
      id: 'hatch-patent',
      caseId: 'hatch-waxman',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '专利：用排他期换研发激励',
      summary: '专利让创新药在一段时间内独占市场，以补偿高固定成本与失败风险。',
      detail:
        '新药研发成本高、失败率高，而仿制成本低。如果没有专利保护，竞争者可以在研发成功后迅速复制，削弱投资激励。专利期是一种制度性的“高价窗口”，但窗口多长、如何补偿审批损耗，都是政策选择。',
      boundary: '专利只保护权利要求覆盖的内容；改进、组合、给药方式等周边专利可能延长实际保护，但不一定延长基础分子专利。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fda-hatch-waxman'],
    },
    {
      id: 'hatch-approval',
      caseId: 'hatch-waxman',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '审批：时间也是成本',
      summary: '新药审批消耗专利期，法案用专利期补偿与简化仿制路径来平衡。',
      detail:
        '如果审批需要十年，专利期就只剩很短的商业窗口；如果仿制药必须重复全部临床试验，进入成本又过高。Hatch-Waxman 用专利期恢复和 ANDA 路径，把审批时间与竞争时点重新安排。',
      boundary: '审批速度、专利期限和专利挑战时点相互作用；不同药物与适应症的实际情况差异很大。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fda-hatch-waxman'],
    },
    {
      id: 'hatch-generic',
      caseId: 'hatch-waxman',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '仿制药：用生物等效性替代重复试验',
      summary: 'ANDA 路径允许仿制药证明生物等效，而不必重复完整临床试验。',
      detail:
        '仿制药企业需要证明产品与参比制剂在活性成分、剂型、剂量和生物利用度上等效，并满足生产质量要求。这大幅降低了进入成本，让专利到期后的价格竞争更快发生。',
      boundary: '生物等效不等于临床可互换的全部情形；窄治疗窗、复杂制剂和生物药适用不同规则。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fda-hatch-waxman'],
    },
    {
      id: 'hatch-exclusivity',
      caseId: 'hatch-waxman',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '首仿独占：奖励挑战，也制造排队',
      summary: '首个成功挑战专利的仿制药可获得一段独占期，其他仿制药进入被推迟。',
      detail:
        '首仿独占鼓励企业挑战弱专利，但也可能被用来排他：首个申请者可以通过和解、延迟或供应安排影响后续竞争。专利链接与 30 个月诉讼暂停等机制，让法律时点直接改变市场进入时间。',
      boundary: '独占期的具体适用、 forfeiture 与和解受法条、判例和 FDA 决定约束。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fda-hatch-waxman', 'ftc-generic-drugs'],
    },
    {
      id: 'hatch-payer',
      caseId: 'hatch-waxman',
      kind: 'mechanism',
      stage: 2,
      lane: 0,
      title: '支付方：保险与采购放大价格差异',
      summary: '患者自付、保险覆盖、PBM 与采购规则决定专利药和仿制药的实际使用。',
      detail:
        '药品标价不等于患者支付价。保险 formulary、共付额、药品福利管理（PBM）返点与医院采购都会改变用量。即使仿制药便宜，处方习惯、品牌信任和支付规则也可能让它进入得慢。',
      boundary: '美国支付体系高度复杂，联邦医保、商业保险与现金支付的行为不同。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['ftc-generic-drugs'],
    },
    {
      id: 'hatch-outcome',
      caseId: 'hatch-waxman',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：仿制药普及与专利博弈',
      summary: '专利到期后价格通常下降，但创新药与仿制药的边界持续被诉讼和策略重画。',
      detail:
        'Hatch-Waxman 让仿制药在专利到期后更快进入，也让企业围绕专利组合、授权、和解和首仿独占展开长期博弈。结果是双重的：可及性提高，但专利常青化、授权延迟和复杂制剂可能延缓竞争。',
      boundary: '不同药物类别的竞争程度差异巨大；生物药、复杂仿制药与普通小分子药不能用同一套结论。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['fda-hatch-waxman', 'ftc-generic-drugs'],
    },
    {
      id: 'hatch-risk',
      caseId: 'hatch-waxman',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：用法律时点替代真实创新',
      summary: '当专利组合、和解和审批策略比研发更影响利润，制度可能奖励“拖延竞争”。',
      detail:
        '如果企业可以通过增加周边专利、与仿制药商和解或利用审批程序延长独占，消费者支付的高价未必对应更多突破性创新。监管与执法需要区分“保护发明”和“延长垄断”。',
      boundary: '专利策略也有合法与促进投资的面向；不能把所有专利组合都视为滥用。',
      evidence: 'opinion',
      confidence: 'high',
      sourceIds: ['ftc-generic-drugs'],
    },
    {
      id: 'hatch-opp',
      caseId: 'hatch-waxman',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：合规与准入的复杂劳动',
      summary: '药品进入市场需要法规、专利、质量、医保与供应链的专业协调。',
      detail:
        '机会包括：仿制药与复杂制剂的法规事务、专利检索与挑战支持、质量体系与供应链审计、药物经济学与医保准入。它们不承诺高收益，但属于高门槛、可积累的专业服务。',
      boundary: '高度受监管，需要专业资质与责任边界；不能把一般分析包装成法律或医疗建议。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['fda-hatch-waxman'],
    },
  ],
  edges: [
    {
      id: 'hatch-e1',
      caseId: 'hatch-waxman',
      from: 'hatchwaxman',
      to: 'hatch-patent',
      label: '专利保护',
      explanation: '用一段排他期补偿新药研发的高固定成本与失败风险。',
      tone: 'cause',
    },
    {
      id: 'hatch-e2',
      caseId: 'hatch-waxman',
      from: 'hatchwaxman',
      to: 'hatch-approval',
      label: '审批与专利期的冲突',
      explanation: '审批时间消耗专利商业期，于是出现专利期补偿与简化路径。',
      tone: 'cause',
    },
    {
      id: 'hatch-e3',
      caseId: 'hatch-waxman',
      from: 'hatch-approval',
      to: 'hatch-generic',
      label: '简化仿制申请',
      explanation: 'ANDA 路径允许用生物等效性证明替代重复临床试验。',
      tone: 'enable',
    },
    {
      id: 'hatch-e4',
      caseId: 'hatch-waxman',
      from: 'hatch-generic',
      to: 'hatch-exclusivity',
      label: '首仿挑战',
      explanation: '首个成功挑战专利的仿制药可获得独占期，其他申请者进入被推迟。',
      tone: 'cause',
    },
    {
      id: 'hatch-e5',
      caseId: 'hatch-waxman',
      from: 'hatch-generic',
      to: 'hatch-outcome',
      label: '价格竞争',
      explanation: '专利到期与仿制药进入通常带来价格下降和用量变化。',
      tone: 'cause',
    },
    {
      id: 'hatch-e6',
      caseId: 'hatch-waxman',
      from: 'hatch-exclusivity',
      to: 'hatch-outcome',
      label: '进入时点被法律重写',
      explanation: '专利链接、诉讼暂停与独占期决定仿制药何时能真正销售。',
      tone: 'cause',
    },
    {
      id: 'hatch-e7',
      caseId: 'hatch-waxman',
      from: 'hatch-payer',
      to: 'hatch-outcome',
      label: '支付规则决定用量',
      explanation: '保险、共付与采购规则让价格信号不能直接决定处方。',
      tone: 'enable',
    },
    {
      id: 'hatch-e8',
      caseId: 'hatch-waxman',
      from: 'hatch-patent',
      to: 'hatch-risk',
      label: '保护可能变成拖延',
      explanation: '周边专利、和解与程序策略可能延长实际独占，而不增加真实创新。',
      tone: 'risk',
    },
    {
      id: 'hatch-e9',
      caseId: 'hatch-waxman',
      from: 'hatch-risk',
      to: 'hatch-opp',
      label: '复杂性变成专业服务',
      explanation: '法规、专利与支付复杂性，创造了合规、检索与准入服务的需求。',
      tone: 'enable',
    },
  ],
};
